import { useCallback, useMemo, useState } from 'react'

import {
  PurchaseOrdersDataCollectionDocument,
  UpdatePurchaseOrderStatusDocument,
} from 'generated/resources/graphql'
import type {
  ProcurementValueObjectsPurchaseOrderStatusEnum,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export function useStatusTransition(purchaseOrder: PurchaseOrderDetailFieldsFragment) {
  const { company } = useCurrentSession()
  const [updatingStatus, setUpdatingStatus] = useState(false)

  const [updatePurchaseOrderStatus] = useMutation(UpdatePurchaseOrderStatusDocument, {
    extractorFn: (data) => data.procurementMutations.updateStatusProcurementPurchaseOrder,
  })

  const handleUpdateStatus = useCallback(
    async (
      updatedStatus: ProcurementValueObjectsPurchaseOrderStatusEnum,
      successMessage: string
    ) => {
      setUpdatingStatus(true)
      const result = await updatePurchaseOrderStatus({
        variables: {
          companyId: company.id,
          id: purchaseOrder.id,
          status: updatedStatus,
        },
        refetchQueries: [PurchaseOrdersDataCollectionDocument],
      })
      setUpdatingStatus(false)

      const handled = handleResult(result, () => {
        app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
      })

      if (handled.success) {
        app.setFlash({ type: 'correct', message: successMessage })
      }
    },
    [company.id, purchaseOrder.id, updatePurchaseOrderStatus]
  )

  return useMemo(
    () => ({ updatingStatus, handleUpdateStatus }),
    [updatingStatus, handleUpdateStatus]
  )
}
